import { PlusCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Button, notification, Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IBeneficiaryStatusLookupListTableRow } from '../../../../../interfaces/lookup/beneficiaryStatus';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { NOTIFICATION_PLACEMENT } from '../../../../../shared/Constants';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchBeneficiaryStatuses } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';

export const BeneficiaryStatusLookupListOverview = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { beneficiaryStatuses } = useSelector((state: RootState) => state.lookupSlice);

    const columns: ColumnsType<IBeneficiaryStatusLookupListTableRow> = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            render: (_, record) => (
                <>
                    <Tooltip title={record.description}>{record.code}</Tooltip>
                </>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="text"
                        icon={<RightOutlined />}
                        onClick={() => navigate(`/admin/lookup-lists/beneficiary-status/${record.id}`)}
                    />
                </Space>
            ),
        },
    ];

    useEffect(() => {
        if (beneficiaryStatuses.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/beneficiary-status', { replace: true });
        }
    }, [beneficiaryStatuses.updateStatus]);

    useEffect(() => {
        dispatch(fetchBeneficiaryStatuses());

        return () => {
            dispatch(resetState());
        };
    }, []);

    useEffect(() => {
        if (beneficiaryStatuses.fetchStatus === StatePieceStatus.Error) {
            notification.error({
                message: 'An error occurred.',
                duration: 0,
                description: 'An error occurred while retrieving the data. Please contact your administrator if the problem persists.',
                placement: NOTIFICATION_PLACEMENT,
            });
        }
    }, [beneficiaryStatuses.fetchStatus]);

    return (
        <div className="app-page">
            <ContentHeader
                title="Manage beneficiary status lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Beneficiary status',
                        path: '/admin/lookup-lists/beneficiary-status',
                    },
                ]}
            >
                <>
                    <Button
                        type="primary"
                        icon={<PlusCircleOutlined />}
                        onClick={() => navigate('/admin/lookup-lists/beneficiary-status/add')}
                    >
                        Add new option
                    </Button>
                </>
            </ContentHeader>

            <div className="page-content">
                <Table
                    size="small"
                    bordered={true}
                    loading={beneficiaryStatuses.fetchStatus === StatePieceStatus.IsFetching}
                    dataSource={beneficiaryStatuses.data.map((option, i) => {
                        return { ...option, key: i } as IBeneficiaryStatusLookupListTableRow;
                    })}
                    columns={columns}
                    pagination={{
                        defaultPageSize: 50,
                    }}
                />
            </div>
        </div>
    );
};
